import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  updateProviderKeySchema,
  type UpdateProviderKeyInput,
} from '@/lib/schemas/ai-schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface UpdateKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  providerName: string | null;
  onSubmit: (apiKey: string) => void;
  isPending: boolean;
}

export function UpdateKeyDialog({ isOpen, onClose, providerName, onSubmit, isPending }: UpdateKeyDialogProps) {
  const form = useForm<UpdateProviderKeyInput>({
    resolver: zodResolver(updateProviderKeySchema),
    defaultValues: { apiKey: '' },
  });

  function handleClose() {
    form.reset({ apiKey: '' });
    onClose();
  }

  function handleSubmit(data: UpdateProviderKeyInput) {
    onSubmit(data.apiKey);
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="update-key-dialog">
        <DialogHeader>
          <DialogTitle>Update API Key</DialogTitle>
          <DialogDescription>
            Update the API key for <strong>{providerName}</strong>.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="uk-key">New API Key</Label>
            <Input id="uk-key" type="password" placeholder="sk-..." {...form.register('apiKey')} data-testid="uk-key" />
            {form.formState.errors.apiKey && (
              <p className="text-sm text-red-600">{form.formState.errors.apiKey.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Updating...' : 'Update Key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
